/**
 * File Picker Store
 * Zustand store for universal file picker system
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { LibraryItem } from "@/api/models/api-types";
import type {
  FilePickerStore,
  SelectedFile,
  PickerConfig,
  PickerOrigin,
  PickerSource,
  PickerUpload,
} from "../types/file-picker-types";
import useAuthStore from "@/store/use-auth-store";
import { useAuthGateStore } from "@/features/auth-gate/store/auth-gate-store";

const DEFAULT_MAX_SELECTION = 10;
const MAX_RECENT_ITEMS = 24;
const EMPTY_RECENT: LibraryItem[] = [];

const getUserId = () => useAuthStore.getState().user?._id ?? null;

// Her açılışta sıfırlanan durum
const sessionState = {
  config: null,
  source: "all" as PickerSource,
  folderId: null,
  origin: "any" as PickerOrigin,
  search: "",
  typeFilter: null,
  categoryFilter: null,
  quickLookId: null,
  selectedFiles: [],
  selectedItems: {},
  uploads: [],
  isLoading: false,
  isUploading: false,
};

const initialState = {
  ...sessionState,
  isOpen: false,
  recentByUser: {},
  isGoogleAuthed: false,
  googleAccessToken: null,
};

const revokePreviews = (uploads: PickerUpload[]) => {
  uploads.forEach((upload) => upload.previewUrl && URL.revokeObjectURL(upload.previewUrl));
};

// ===== File Picker Store =====

export const useFilePickerStore = create<FilePickerStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ===== Modal Actions =====

      openPicker: (config: PickerConfig) => {
        // 🔓 Public mod: dosya kütüphanesi ve upload auth gerektirir;
        // guest picker'ı açmaya çalışırsa AuthGateDialog gösterilir.
        const { user, refreshToken, isTokenExpired } = useAuthStore.getState();
        const hasSession = !!(user && (!isTokenExpired() || refreshToken));
        if (!hasSession) {
          useAuthGateStore.getState().openAuthGate();
          return;
        }

        revokePreviews(get().uploads);
        set({ ...sessionState, isOpen: true, config });
      },

      closePicker: (reason = "cancel") => {
        const { config, uploads } = get();

        // Only run cancel callback for explicit dismiss/cancel flows.
        if (reason === "cancel" && config?.onCancel) {
          config.onCancel();
        }

        revokePreviews(uploads);
        set({ ...sessionState, isOpen: false });
      },

      confirmSelection: () => {
        const { config, selectedFiles, selectedItems, recentByUser } = get();
        if (config?.onSelect && selectedFiles.length > 0) {
          config.onSelect(selectedFiles);
        }

        const userId = getUserId();
        const picked = selectedFiles
          .map((file) => selectedItems[file.id])
          .filter((item): item is LibraryItem => !!item);
        if (userId && picked.length > 0) {
          const pickedIds = new Set(picked.map((item) => item._id));
          const previous = (recentByUser[userId] ?? []).filter((item) => !pickedIds.has(item._id));
          set({
            recentByUser: {
              ...recentByUser,
              [userId]: [...picked, ...previous].slice(0, MAX_RECENT_ITEMS),
            },
          });
        }

        get().closePicker("confirm");
      },

      // ===== Navigation Actions =====

      setSource: (source: PickerSource, folderId: string | null = null) => {
        set({ source, folderId: source === "folder" ? folderId : null, quickLookId: null });
      },

      setOrigin: (origin: PickerOrigin) => set({ origin }),
      setSearch: (search: string) => set({ search }),
      setTypeFilter: (typeFilter: string | null) => set({ typeFilter }),
      setCategoryFilter: (categoryFilter: string | null) => set({ categoryFilter }),
      setQuickLookId: (quickLookId: string | null) => set({ quickLookId }),

      // ===== File Selection Actions =====

      selectFile: (file: SelectedFile, item?: LibraryItem) => {
        const { selectedFiles, selectedItems, config } = get();
        const maxSelection = config?.maxSelection || DEFAULT_MAX_SELECTION;

        if (selectedFiles.some((f) => f.id === file.id)) return;
        if (selectedFiles.length >= maxSelection) return;

        set({
          selectedFiles: [...selectedFiles, file],
          selectedItems: item ? { ...selectedItems, [file.id]: item } : selectedItems,
        });

        // Tekli seçimde seçim anında onaylanır
        if (maxSelection === 1) {
          get().confirmSelection();
        }
      },

      removeFile: (fileId: string) => {
        const { selectedFiles, selectedItems } = get();
        const nextItems = { ...selectedItems };
        delete nextItems[fileId];
        set({
          selectedFiles: selectedFiles.filter((f) => f.id !== fileId),
          selectedItems: nextItems,
        });
      },

      clearSelection: () => {
        set({ selectedFiles: [], selectedItems: {} });
      },

      // ===== Upload Queue =====

      addUpload: (upload: PickerUpload) => {
        set({ uploads: [upload, ...get().uploads] });
      },

      updateUpload: (id: string, patch: Partial<PickerUpload>) => {
        set({
          uploads: get().uploads.map((upload) => (upload.id === id ? { ...upload, ...patch } : upload)),
        });
      },

      removeUpload: (id: string) => {
        const { uploads } = get();
        revokePreviews(uploads.filter((upload) => upload.id === id));
        set({ uploads: uploads.filter((upload) => upload.id !== id) });
      },

      // ===== Google Drive Actions =====

      setGoogleAuth: (token: string | null) => {
        set({
          googleAccessToken: token,
          isGoogleAuthed: !!token,
        });
      },

      // ===== Loading Actions =====

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setUploading: (uploading: boolean) => {
        set({ isUploading: uploading });
      },

      // ===== Reset Action =====

      reset: () => {
        revokePreviews(get().uploads);
        set({ ...sessionState, isOpen: false });
      },
    }),
    {
      name: "file-picker-storage",
      partialize: (state) => ({
        isGoogleAuthed: state.isGoogleAuthed,
        googleAccessToken: state.googleAccessToken,
        recentByUser: state.recentByUser,
      }),
    },
  ),
);

// ===== Selector Hooks =====

export const useFilePickerModal = () => {
  const isOpen = useFilePickerStore((state) => state.isOpen);
  const config = useFilePickerStore((state) => state.config);
  const openPicker = useFilePickerStore((state) => state.openPicker);
  const closePicker = useFilePickerStore((state) => state.closePicker);
  const confirmSelection = useFilePickerStore((state) => state.confirmSelection);
  return { isOpen, config, openPicker, closePicker, confirmSelection };
};

export const useFilePickerSelection = () => {
  const selectedFiles = useFilePickerStore((state) => state.selectedFiles);
  const selectFile = useFilePickerStore((state) => state.selectFile);
  const removeFile = useFilePickerStore((state) => state.removeFile);
  const clearSelection = useFilePickerStore((state) => state.clearSelection);
  const maxSelection = useFilePickerStore(
    (state) => state.config?.maxSelection || DEFAULT_MAX_SELECTION
  );
  return {
    selectedFiles,
    selectFile,
    removeFile,
    clearSelection,
    maxSelection,
    canSelectMore: selectedFiles.length < maxSelection,
  };
};

export const useFilePickerNavigation = () => {
  const source = useFilePickerStore((state) => state.source);
  const folderId = useFilePickerStore((state) => state.folderId);
  const setSource = useFilePickerStore((state) => state.setSource);
  return { source, folderId, setSource };
};

/** Oturumdaki kullanıcının seçicide son onayladığı öğeler */
export const usePickerRecentItems = () => {
  const userId = useAuthStore((state) => state.user?._id);
  return useFilePickerStore((state) =>
    userId ? state.recentByUser[userId] ?? EMPTY_RECENT : EMPTY_RECENT
  );
};

export const useFilePickerGoogle = () => {
  const isGoogleAuthed = useFilePickerStore((state) => state.isGoogleAuthed);
  const googleAccessToken = useFilePickerStore((state) => state.googleAccessToken);
  const setGoogleAuth = useFilePickerStore((state) => state.setGoogleAuth);
  return { isGoogleAuthed, googleAccessToken, setGoogleAuth };
};

export const useFilePickerLoading = () => {
  const isLoading = useFilePickerStore((state) => state.isLoading);
  const isUploading = useFilePickerStore((state) => state.isUploading);
  const setLoading = useFilePickerStore((state) => state.setLoading);
  const setUploading = useFilePickerStore((state) => state.setUploading);
  return { isLoading, isUploading, setLoading, setUploading };
};
